// Time Furniture CRM — Sales Reference Library media tab + thread picker.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Upload, Link2, Trash2, Pencil, Send, X, FileText, RefreshCw, BarChart3 } from "lucide-react";
import { crmApi } from "../../lib/crmApi";

const inputCls = "w-full border border-[#6E1F2C]/20 bg-[#FAF5EC] px-2.5 py-1.5 text-[12px] text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
const VIS_STYLE = {
  internal_only: "text-[#A3141A] border-[#A3141A]/40",
  customer_approved: "text-[#3F6E2C] border-[#3F6E2C]/40",
  public: "text-[#2C5F6E] border-[#2C5F6E]/40",
};
const EMPTY_FORM = {
  media_type: "project_photo", title: "", caption: "", service: "", style: "", colors: "",
  materials: "", room_type: "", property_type: "", budget_category: "", emirate: "", community: "",
  project_name: "", completion_status: "completed", source_url: "", tags: [],
  visibility: "internal_only", active: true,
};

export function LibThumb({ item, size = "h-24" }) {
  const [url, setUrl] = useState(null);
  const isImg = (item.file_mime || "").startsWith("image/");
  useEffect(() => {
    let live = true;
    if (item.file_media_id && isImg) crmApi.fetchMediaUrl(item.file_media_id).then((u) => live && setUrl(u)).catch(() => {});
    return () => { live = false; };
  }, [item.file_media_id, isImg]);
  if (url) return <img src={url} alt="" className={`${size} w-full object-cover`} />;
  return (
    <div className={`${size} w-full bg-[#6E1F2C]/8 flex items-center justify-center text-[#6E1F2C]`}>
      {item.file_media_id ? <FileText size={20} /> : <Link2 size={20} />}
    </div>
  );
}

function ItemCard({ m, onEdit, onToggle, onDelete, onAutoTag, busy }) {
  return (
    <div className="border border-[#6E1F2C]/15 bg-white" data-testid={`lib-item-${m.id}`}>
      <LibThumb item={m} />
      <div className="p-2 space-y-1">
        <p className="text-[11px] font-medium text-[#1C110C] leading-tight truncate" title={m.title}>{m.title}</p>
        <div className="flex flex-wrap gap-1">
          <span className={`text-[8px] uppercase border px-1 ${
            m.media_type === "generated_concept" ? "text-[#A6822F] border-[#A6822F]/50 bg-[#A6822F]/10"
            : ["project_photo", "before_after"].includes(m.media_type) ? "text-[#3F6E2C] border-[#3F6E2C]/40"
            : "text-[#2C5F6E] border-[#2C5F6E]/30"}`} data-testid="lib-item-origin">
            {m.media_type === "generated_concept" ? "AI concept"
              : ["project_photo", "before_after"].includes(m.media_type) ? `TF project · ${(m.media_type || "").replace(/_/g, " ")}`
              : (m.media_type || "").replace(/_/g, " ")}
          </span>
          <span className={`text-[8px] uppercase border px-1 ${VIS_STYLE[m.visibility] || VIS_STYLE.internal_only}`} data-testid="lib-item-visibility">
            {(m.visibility || "internal_only").replace(/_/g, " ")}
          </span>
          {!m.active && <span className="text-[8px] uppercase text-[#7A6B5F] border border-[#7A6B5F]/40 px-1">inactive</span>}
        </div>
        <p className="text-[9px] text-[#7A6B5F] truncate">{[m.style, m.room_type, m.emirate].filter(Boolean).join(" · ")}</p>
        {m.times_sent > 0 && <p className="text-[9px] text-[#3F6E2C]">Sent {m.times_sent}×</p>}
        <div className="flex gap-1 pt-0.5">
          <button type="button" title="Edit" onClick={() => onEdit(m)} className="border border-[#6E1F2C]/25 text-[#6E1F2C] p-1 hover:bg-[#6E1F2C]/10" data-testid="lib-item-edit"><Pencil size={10} /></button>
          {m.file_media_id && (m.file_mime || "").startsWith("image/") && (
            <button type="button" title="AI auto-tag" onClick={() => onAutoTag(m)} disabled={busy === m.id}
              className="border border-[#A6822F]/40 text-[#A6822F] p-1 hover:bg-[#A6822F]/10 disabled:opacity-40" data-testid="lib-item-autotag">
              <Sparkles size={10} />
            </button>
          )}
          <button type="button" title={m.active ? "Deactivate" : "Activate"} onClick={() => onToggle(m)}
            className="border border-[#7A6B5F]/30 text-[#7A6B5F] p-1 text-[8px] uppercase hover:bg-[#7A6B5F]/10">{m.active ? "on" : "off"}</button>
          <button type="button" title="Delete" onClick={() => onDelete(m)} className="border border-[#A3141A]/30 text-[#A3141A] p-1 hover:bg-[#A3141A]/10 ml-auto"><Trash2 size={10} /></button>
        </div>
      </div>
    </div>
  );
}

export function LibraryMediaTab() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (typeF) p.set("media_type", typeF);
    crmApi.mediaList(`?${p}`).then((d) => { setItems(d.items); setTypes(d.types); }).catch((e) => setMsg(e.message));
  }, [q, typeF]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const body = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (editId) await crmApi.mediaUpdate(editId, body);
      else if (file) await crmApi.libUpload(file, body);
      else await crmApi.mediaCreate(body);
      setForm(EMPTY_FORM); setFile(null); setEditId(null); setMsg("Saved ✓"); load();
    } catch (e) { setMsg(e.message); }
    setBusy("");
  };
  const autoTag = async (m) => {
    setBusy(m.id); setMsg("AI analysing image…");
    try {
      const { suggestions } = await crmApi.libAutoTag(m.id);
      await crmApi.mediaUpdate(m.id, {
        ...m,
        title: m.title || suggestions.title,
        service: m.service || suggestions.service,
        room_type: m.room_type || suggestions.room_type,
        style: m.style || suggestions.style,
        colors: m.colors || suggestions.colors,
        materials: m.materials || suggestions.materials,
        property_type: m.property_type || suggestions.property_type,
        tags: [...new Set([...(m.tags || []), ...(suggestions.tags || [])])],
        ai_description: suggestions.ai_description,
      });
      setMsg(`Auto-tagged ✓ — ${(suggestions.tags || []).slice(0, 6).join(", ")}`);
      load();
    } catch (e) { setMsg(e.message); }
    setBusy("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="lib-search" className={`${inputCls} max-w-xs`} placeholder='Semantic search… e.g. "modern tv wall oak"'
          value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={`${inputCls} max-w-[180px]`} value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="lib-type-filter">
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
        <button type="button" onClick={load} className="border border-[#7A6B5F]/30 text-[#7A6B5F] p-1.5 hover:bg-[#7A6B5F]/10"><RefreshCw size={12} /></button>
        <button type="button" data-testid="lib-stats-btn"
          onClick={() => (stats ? setStats(null) : crmApi.libStats().then((d) => setStats(d.stats)).catch((e) => setMsg(e.message)))}
          className="border border-[#2C5F6E]/40 text-[#2C5F6E] px-2 py-1.5 text-[9px] uppercase tracking-[0.12em] hover:bg-[#2C5F6E]/10 inline-flex items-center gap-1">
          <BarChart3 size={11} /> {stats ? "Hide stats" : "Effectiveness"}
        </button>
        {msg && <p className="text-[11px] text-[#A6822F]" data-testid="lib-msg">{msg}</p>}
      </div>

      {stats && (
        <div className="border border-[#2C5F6E]/20 bg-white p-3" data-testid="lib-stats">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[#2C5F6E] mb-2">Reference effectiveness — which items convert</p>
          {stats.length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">No references sent yet.</p>}
          {stats.map((s) => (
            <div key={s.item_id} className="flex justify-between gap-2 text-[11px] border-b border-[#2C5F6E]/10 py-1">
              <span className="truncate text-[#1C110C]">{s.title}</span>
              <span className="shrink-0 text-[#7A6B5F]">sent {s.times_sent}× · {s.unique_leads} leads · {s.site_visit_or_later} advanced · {s.won} won</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 content-start max-h-[65vh] overflow-y-auto" data-testid="lib-grid">
          {items.map((m) => (
            <ItemCard key={m.id} m={m} busy={busy}
              onEdit={(it) => { setEditId(it.id); setForm({ ...EMPTY_FORM, ...it, tags: it.tags || [] }); }}
              onToggle={(it) => crmApi.mediaUpdate(it.id, { ...it, active: !it.active }).then(load)}
              onDelete={(it) => window.confirm(`Delete "${it.title}"?`) && crmApi.mediaDelete(it.id).then(load)}
              onAutoTag={autoTag} />
          ))}
          {items.length === 0 && <p className="text-xs text-[#7A6B5F] italic col-span-full">No references yet — upload project photos or add website/Instagram links.</p>}
        </div>

        <div className="border border-[#6E1F2C]/15 bg-white p-4 space-y-2 h-fit">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F]">
            {editId ? "Edit reference" : "Add reference (upload file or link)"}
          </p>
          {!editId && (
            <div className="flex items-center gap-2">
              <input type="file" ref={fileRef} hidden onChange={(e) => setFile(e.target.files?.[0] || null)}
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" />
              <button type="button" data-testid="lib-upload-btn" onClick={() => fileRef.current?.click()}
                className="border border-[#6E1F2C]/30 text-[#6E1F2C] px-2 py-1.5 text-[9px] uppercase tracking-[0.12em] hover:bg-[#6E1F2C]/10 inline-flex items-center gap-1">
                <Upload size={11} /> {file ? file.name.slice(0, 24) : "Choose file"}
              </button>
              {file && <button type="button" onClick={() => setFile(null)} className="text-[#A3141A]"><X size={12} /></button>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="media-type" className={inputCls} value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })}>
              {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
            <select data-testid="lib-visibility" className={inputCls} value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
              <option value="internal_only">Internal only</option>
              <option value="customer_approved">Customer approved</option>
              <option value="public">Public</option>
            </select>
          </div>
          <input data-testid="media-title" className={inputCls} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input data-testid="media-caption" className={inputCls} placeholder="WhatsApp caption" value={form.caption || ""} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Service (e.g. curtains_blinds)" value={form.service || ""} onChange={(e) => setForm({ ...form, service: e.target.value })} />
            <input className={inputCls} placeholder="Room (living room…)" value={form.room_type || ""} onChange={(e) => setForm({ ...form, room_type: e.target.value })} />
            <input className={inputCls} placeholder="Style (japandi, modern…)" value={form.style || ""} onChange={(e) => setForm({ ...form, style: e.target.value })} />
            <input className={inputCls} placeholder="Colours" value={form.colors || ""} onChange={(e) => setForm({ ...form, colors: e.target.value })} />
            <input className={inputCls} placeholder="Materials (oak, walnut…)" value={form.materials || ""} onChange={(e) => setForm({ ...form, materials: e.target.value })} />
            <input className={inputCls} placeholder="Property type" value={form.property_type || ""} onChange={(e) => setForm({ ...form, property_type: e.target.value })} />
            <input className={inputCls} placeholder="Emirate" value={form.emirate || ""} onChange={(e) => setForm({ ...form, emirate: e.target.value })} />
            <input className={inputCls} placeholder="Community (Dubai Hills…)" value={form.community || ""} onChange={(e) => setForm({ ...form, community: e.target.value })} />
            <input className={inputCls} placeholder="Project name" value={form.project_name || ""} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
            <input className={inputCls} placeholder="Budget category" value={form.budget_category || ""} onChange={(e) => setForm({ ...form, budget_category: e.target.value })} />
          </div>
          <input data-testid="media-url" className={inputCls} placeholder="URL (website / Instagram / YouTube)" value={form.source_url || ""} onChange={(e) => setForm({ ...form, source_url: e.target.value })} />
          <input data-testid="lib-tags" className={inputCls} placeholder="Tags, comma separated"
            value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <div className="flex gap-2">
            <button type="button" data-testid="media-save" onClick={save} disabled={!form.title || busy === "save"}
              className="bg-[#6E1F2C] text-white px-4 py-2 text-[9px] tracking-[0.16em] uppercase disabled:opacity-50">
              {editId ? "Update" : file ? "Upload & save" : "Save link"}
            </button>
            {editId && <button type="button" onClick={() => { setEditId(null); setForm(EMPTY_FORM); }}
              className="border border-[#7A6B5F]/30 text-[#7A6B5F] px-3 py-2 text-[9px] uppercase">Cancel</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------- thread "Send from Library" picker
export function LibraryPicker({ leadId, onClose, onSent }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      crmApi.mediaList(`?${new URLSearchParams(q ? { q } : {})}`)
        .then((d) => setItems(d.items.filter((m) => m.active && m.visibility !== "internal_only")))
        .catch((e) => setMsg(e.message));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(0, 5)));
  const send = async () => {
    setBusy(true);
    try {
      const r = await crmApi.libSend(leadId, sel, caption);
      setMsg(r.ok ? `Sent ${r.sent}/${r.total} ✓` : `Sent ${r.sent}/${r.total} — ${r.results.filter((x) => !x.ok).map((x) => x.error).join("; ")}`);
      onSent?.();
      if (r.ok) setTimeout(onClose, 900);
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="lib-picker">
      <div className="bg-[#FAF5EC] border border-[#6E1F2C]/20 w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#6E1F2C]/10">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#6E1F2C]">Send from Library</p>
          <button type="button" onClick={onClose} data-testid="lib-picker-close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={16} /></button>
        </div>
        <div className="px-4 py-2">
          <input autoFocus data-testid="lib-picker-search" className={inputCls}
            placeholder='Search references… e.g. "luxury villa curtains", "modern kitchen"' value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex-1 overflow-y-auto px-4 grid grid-cols-3 md:grid-cols-4 gap-2 content-start" data-testid="lib-picker-grid">
          {items.map((m) => (
            <button key={m.id} type="button" onClick={() => toggle(m.id)}
              className={`text-left border ${sel.includes(m.id) ? "border-[#6E1F2C] ring-2 ring-[#6E1F2C]/40" : "border-[#6E1F2C]/15"} bg-white`}
              data-testid={`lib-pick-${m.id}`}>
              <LibThumb item={m} size="h-20" />
              <p className="text-[9px] text-[#1C110C] p-1 truncate">{m.title}</p>
            </button>
          ))}
          {items.length === 0 && <p className="text-[11px] text-[#7A6B5F] italic col-span-full py-4">No customer-approved references match.</p>}
        </div>
        <div className="px-4 py-3 border-t border-[#6E1F2C]/10 space-y-2">
          <input data-testid="lib-picker-caption" className={inputCls} placeholder="Custom caption for the first item (optional)"
            value={caption} onChange={(e) => setCaption(e.target.value)} />
          <div className="flex items-center gap-3">
            <button type="button" disabled={!sel.length || busy} onClick={send} data-testid="lib-picker-send"
              className="bg-[#6E1F2C] text-white px-4 py-2 text-[9px] tracking-[0.16em] uppercase disabled:opacity-50 inline-flex items-center gap-1.5">
              <Send size={11} /> Send {sel.length || ""} reference{sel.length !== 1 ? "s" : ""}
            </button>
            {msg && <p className="text-[11px] text-[#A6822F]" data-testid="lib-picker-msg">{msg}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------- thread sidebar AI suggestions
export function SuggestedRefs({ leadId, onOpenPicker }) {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => crmApi.libSuggest(leadId).then((d) => setItems(d.items)).catch(() => {}), [leadId]);
  useEffect(() => { load(); }, [load]);
  const sendOne = async (id) => {
    setMsg("Sending…");
    try { await crmApi.mediaShare(id, leadId); setMsg("Sent ✓"); } catch (e) { setMsg(e.message); }
  };
  return (
    <div className="border border-[#A6822F]/25 bg-white p-3" data-testid="wa-suggested-refs">
      <div className="flex items-center justify-between">
        <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F]">AI suggested references</p>
        <button type="button" onClick={onOpenPicker} className="text-[9px] uppercase text-[#6E1F2C] hover:underline" data-testid="wa-open-library-from-suggest">Library →</button>
      </div>
      {items.length === 0 && <p className="text-[10px] text-[#7A6B5F] italic mt-1">No matching approved references yet.</p>}
      {items.map((m) => (
        <div key={m.id} className="flex items-center gap-2 mt-2" data-testid="wa-suggested-ref-item">
          <div className="w-10 shrink-0"><LibThumb item={m} size="h-10" /></div>
          <p className="text-[10px] text-[#1C110C] truncate flex-1" title={m.title}>{m.title}</p>
          <button type="button" onClick={() => sendOne(m.id)} title="Send to customer"
            className="border border-[#6E1F2C]/30 text-[#6E1F2C] p-1 hover:bg-[#6E1F2C]/10" data-testid="wa-suggested-ref-send"><Send size={10} /></button>
        </div>
      ))}
      {msg && <p className="text-[10px] text-[#A6822F] mt-1">{msg}</p>}
    </div>
  );
}
